/*
 * Persistence for the Instant Task Alert pop-up (wireframe 03).
 *
 * Two things survive a reload: which corner the rep dragged the pop-up to, and
 * which instant tasks they have already dismissed. Both are per-rep preferences,
 * not shared state, so they live in localStorage under a versioned key following
 * filters_storage.js's convention (versioned key + an in-blob `v` + defensive
 * reads + private-mode try/catch).
 *
 * The key is suffixed with the CMS user uuid. Every other key in this app is
 * origin-global, which is fine for a page filter but wrong here: two reps
 * sharing a browser profile would inherit each other's dismissals and never see
 * an alert they were meant to act on.
 *
 * Dismissal is stored as the SET of task uuids that were pending when the rep
 * dismissed, not as a boolean. The PRD requires the pop-up to reappear on a new
 * instant task "even if prior ones are still open and were previously
 * dismissed", which a boolean cannot express.
 *
 * Every read and write is wrapped: localStorage throws outright in Safari
 * private mode and on a full quota, and a pop-up that cannot remember a corner
 * must still show the alert.
 */

export const POPUP_STORAGE_KEY_PREFIX = 'sales_task_instant_popup_v1';

// Schema version inside the blob. A blob stamped with anything else is
// discarded wholesale — there is no history to migrate yet, and the two values
// here are cheap for a rep to re-establish (drag once, dismiss once).
export const POPUP_SCHEMA_VERSION = 1;

export const CORNERS = ['top-left', 'top-right', 'bottom-left', 'bottom-right'] as const;

export type Corner = (typeof CORNERS)[number];

export interface PopupState {
  corner: Corner;
  dismissed_task_uuids: string[];
}

// The PRD's "pop-up position reset" case: an unknown or missing corner lands
// bottom-right, out of the way of the left nav and the page header.
export const DEFAULT_CORNER: Corner = 'bottom-right';

// A rep who works instant tasks all day would otherwise grow this list without
// bound. Only the most recent dismissals matter — an older uuid is long since
// cleared server-side, so dropping it can at worst re-show an alert that no
// longer exists, which the pending list filters out anyway.
export const MAX_DISMISSED = 200;

// An unauthenticated read (no token yet, or a token this build cannot parse)
// still needs a key rather than an exception: it gets its own bucket, which the
// real user's uuid replaces the moment auth resolves.
export const popupStorageKey = (cmsUserUuid: string | null | undefined): string =>
  `${POPUP_STORAGE_KEY_PREFIX}:${cmsUserUuid || 'anonymous'}`;

export const isCorner = (corner: unknown): corner is Corner =>
  (CORNERS as readonly unknown[]).includes(corner);

const validCorner = (corner: unknown): Corner => (isCorner(corner) ? corner : DEFAULT_CORNER);

const validDismissed = (value: unknown): string[] =>
  Array.isArray(value)
    ? value
        .filter((v): v is string => typeof v === 'string' && v !== '')
        .filter((v, i, arr) => arr.indexOf(v) === i)
        .slice(-MAX_DISMISSED)
    : [];

/*
 * Read this rep's pop-up state.
 *
 * Always returns a usable object — absent, unparseable, wrong-version or
 * partially corrupt storage yields the defaults rather than throwing, because
 * the caller renders the alert either way.
 */
export function readPopupState(cmsUserUuid: string | null | undefined): PopupState {
  const fallback: PopupState = { corner: DEFAULT_CORNER, dismissed_task_uuids: [] };
  let parsed: unknown;
  try {
    const raw = localStorage.getItem(popupStorageKey(cmsUserUuid));
    if (!raw) return fallback;
    parsed = JSON.parse(raw);
  } catch {
    // Unparseable JSON, or localStorage unavailable (private mode).
    return fallback;
  }
  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return fallback;
  const blob = parsed as { v?: unknown; corner?: unknown; dismissed_task_uuids?: unknown };
  if (blob.v !== POPUP_SCHEMA_VERSION) return fallback;
  return {
    corner: validCorner(blob.corner),
    dismissed_task_uuids: validDismissed(blob.dismissed_task_uuids)
  };
}

/*
 * Persist this rep's pop-up state. Best-effort: returns false when storage is
 * unavailable, and the pop-up carries on with its in-memory state.
 */
export function writePopupState(
  cmsUserUuid: string | null | undefined,
  state: Partial<PopupState> = {}
): boolean {
  try {
    localStorage.setItem(
      popupStorageKey(cmsUserUuid),
      JSON.stringify({
        v: POPUP_SCHEMA_VERSION,
        corner: validCorner(state.corner),
        dismissed_task_uuids: validDismissed(state.dismissed_task_uuids)
      })
    );
    return true;
  } catch {
    return false;
  }
}
